// src/services/examAnalyticsService.js
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { getGoetheModuleSummary } from '../utils/goetheExamDisplay';

/**
 * Exam Analytics Service
 * Handles fetching and processing exam history for performance tracking
 */
class ExamAnalyticsService {
  /**
   * Fetch user's exam history with filters
   * @param {string} userId - User ID
   * @param {string} examProvider - TELC, Goethe, OSD
   * @param {string} examLevel - B1, B2, C1
   * @param {number} limitCount - Number of exams to fetch (default: 10)
   * @returns {Promise<Array>} Array of exam results
   */
// In examAnalyticsService.js - Replace getExamHistory method:

async getExamHistory(userId, examProvider, examLevel, limitCount = 10) {
  try {
    console.log(`📊 Fetching exam history: ${examProvider} ${examLevel} (last ${limitCount})`);
    const examsRef = collection(db, 'users', userId, 'examResults');

    // Normalize comparison values (case-insensitive)
    const normalizedProvider = (examProvider || '').toLowerCase().trim();
    const normalizedLevel = (examLevel || '').toLowerCase().trim();

    // Fetch more exams than requested to account for filtering (fetch 2x to ensure we get enough matches)
    // But cap at 100 to avoid performance issues
    const fetchLimit = Math.min(limitCount * 2, 100);

    // ✅ FIXED: Only orderBy, no filters with it
    const q = query(
      examsRef,
      orderBy('completedAt', 'desc'),
      limit(fetchLimit)
    );

    const querySnapshot = await getDocs(q);
    const exams = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // ✅ Filter client-side with case-insensitive comparison
      // Check both 'provider' and 'examProvider' fields for compatibility
      const dataProvider = (data.provider || data.examProvider || '').toLowerCase().trim();
      const dataLevel = (data.level || '').toLowerCase().trim();
      
      if (dataProvider === normalizedProvider && dataLevel === normalizedLevel) {
        exams.push({
          id: doc.id,
          ...data
        });
      }
    });

    // Limit to requested count after filtering
    const limitedExams = exams.slice(0, limitCount);

    console.log(`✅ Fetched ${limitedExams.length} exam results matching ${examProvider} ${examLevel} (from ${querySnapshot.size} total)`);
    return limitedExams.reverse(); // Oldest first
  } catch (error) {
    console.error('Error fetching exam history:', error);
    return [];
  }
}


  /**
   * Calculate overall performance metrics
   * @param {Array} examHistory - Array of exam results
   * @returns {Object} Overall metrics
   */
  calculateOverallMetrics(examHistory) {
    if (!examHistory || examHistory.length === 0) {
      return {
        averageScore: 0,
        bestScore: 0,
        totalExams: 0,
        improvementRate: 0,
        chartData: [],
        goethe: null
      };
    }

    const firstGoethe = getGoetheModuleSummary(examHistory[0]);
    const isGoethe = !!firstGoethe;

    let averageScore = 0;
    let bestScore = 0;
    let goethe = null;

    if (isGoethe) {
      const passedCounts = examHistory.map(exam => {
        const g = getGoetheModuleSummary(exam);
        return g ? g.passed : 0;
      });
      const totalModules = firstGoethe.total;
      const sumPassed = passedCounts.reduce((a, b) => a + b, 0);
      goethe = {
        averagePassed: totalModules > 0 ? Math.round((sumPassed / examHistory.length) * 10) / 10 : 0,
        bestPassed: Math.max(...passedCounts, 0),
        totalModules
      };
      averageScore = goethe.averagePassed;
      bestScore = goethe.bestPassed;
    } else {
      const scores = examHistory.map(exam => exam.overallScore || 0);
      averageScore = scores.reduce((sum, score) => sum + score, 0) / examHistory.length;
      bestScore = Math.max(...scores);
    }

    const totalExams = examHistory.length;
    const scores = examHistory.map(exam => {
      const g = getGoetheModuleSummary(exam);
      if (g && g.total > 0) return (g.passed / g.total) * 100;
      return (exam.overallScore || 0) / (exam.maxScore || 100) * 100;
    });

    let improvementRate = 0;
    if (totalExams >= 2 && scores[0] !== 0) {
      improvementRate = ((scores[scores.length - 1] - scores[0]) / scores[0]) * 100;
    }

    const chartData = examHistory.map((exam, index) => {
      const g = getGoetheModuleSummary(exam);
      const score = g && g.total > 0 ? (g.passed / g.total) * 100 : (exam.overallScore || 0);
      const maxScore = g ? 100 : (exam.maxScore || 100);
      return {
        examNumber: index + 1,
        date: this.formatDate(exam.completedAt),
        score,
        maxScore
      };
    });

    return {
      averageScore: Math.round(averageScore * 10) / 10,
      bestScore,
      totalExams,
      improvementRate: Math.round(improvementRate * 10) / 10,
      chartData,
      goethe
    };
  }

  /**
   * Calculate section-specific metrics
   * @param {Array} examHistory - Array of exam results
   * @returns {Object} Section metrics
   */
calculateSectionMetrics(examHistory) {
  if (!examHistory || examHistory.length === 0) {
    return {
      reading: this.getEmptySectionData(),
      listening: this.getEmptySectionData(),
      writing: this.getEmptySectionData(),
      speaking: this.getEmptySectionData(),
      grammar: this.getEmptySectionData()
    };
  }

  const sections = ['reading', 'listening', 'writing', 'speaking', 'grammar'];
  const sectionMetrics = {};

  sections.forEach(section => {
    // ✅ SPECIAL HANDLING FOR WRITING
    let sectionScores = [];
    let maxScores = [];

    if (section === 'writing') {
      // Writing is stored in writingFeedback, not sectionScores
      const writingData = examHistory.map(exam => {
        const writingFeedback = exam.writingFeedback;
        if (!writingFeedback) return null;
        return {
          score: writingFeedback.rawScore || writingFeedback.scaledScore || writingFeedback.overallScore || 0,
          maxScore: writingFeedback.maxScore || 45
        };
      }).filter(data => data && data.score > 0);

      sectionScores = writingData.map(d => d.score);
      maxScores = writingData.map(d => d.maxScore);
    } else {
      // Regular sections from sectionScores - USE RAW SCORES!
      const sectionData = examHistory.map(exam => {
        const data = exam.sectionScores?.[section];
        if (!data) return null;
        return {
          score: data.score || 0,  // Raw score (e.g., 41.25)
          maxScore: data.maxScore || 100
        };
      }).filter(data => data && data.score > 0);

      sectionScores = sectionData.map(d => d.score);
      maxScores = sectionData.map(d => d.maxScore);
    }

    if (sectionScores.length === 0) {
      sectionMetrics[section] = this.getEmptySectionData();
      return;
    }

    const average = sectionScores.reduce((sum, score) => sum + score, 0) / sectionScores.length;
    const lastScore = sectionScores[sectionScores.length - 1];
    const firstScore = sectionScores[0];
    const maxScore = maxScores[0]; // Use the max score from the section

    // Determine trend
    let trend = 'stable';
    if (sectionScores.length >= 2) {
      const improvement = lastScore - firstScore;
      if (improvement > 2) trend = 'improving';
      else if (improvement < -2) trend = 'declining';
    }

    // Chart data for section - USE RAW SCORES!
    const chartData = examHistory
      .map((exam, index) => {
        let score = 0;
        let maxScore = 100;

        if (section === 'writing') {
          const writingFeedback = exam.writingFeedback;
          if (writingFeedback) {
            score = writingFeedback.rawScore || writingFeedback.scaledScore || writingFeedback.overallScore || 0;
            maxScore = writingFeedback.maxScore || 45;
          }
        } else {
          const sectionData = exam.sectionScores?.[section];
          if (sectionData) {
            score = sectionData.score || 0;  // Raw score
            maxScore = sectionData.maxScore || 100;
          }
        }

        return {
          examNumber: index + 1,
          date: this.formatDate(exam.completedAt),
          score: Math.round(score * 10) / 10,
          maxScore: maxScore
        };
      })
      .filter(data => data.score > 0);

    sectionMetrics[section] = {
      average: Math.round(average * 10) / 10,
      lastScore: Math.round(lastScore * 10) / 10,
      maxScore: maxScore, // Store the max score for display
      trend,
      chartData,
      hasData: true
    };
  });

  return sectionMetrics;
}


  /**
   * Prepare data for radar chart
   * @param {Object} sectionMetrics - Section metrics from calculateSectionMetrics
   * @param {Object} currentExam - Current exam results
   * @returns {Array} Radar chart data
   */
prepareRadarChartData(sectionMetrics, currentExam) {
  const sections = [
    { key: 'reading', label: 'Reading' },
    { key: 'listening', label: 'Listening' },
    { key: 'writing', label: 'Writing' },
    { key: 'speaking', label: 'Speaking' },
    { key: 'grammar', label: 'Grammar' }
  ];

  if (!currentExam) {
    return sections.map(s => ({ subject: s.label, average: 0, current: 0, fullMark: 100 }));
  }

  const radarData = sections
    .map(section => {
      // Get the average score from sectionMetrics
      const avgValue = sectionMetrics[section.key]?.average || 0;

      // Get the current score - special handling for writing
      let currentValue = 0;

      if (section.key === 'writing') {
        // Writing is in writingFeedback
        const writingFeedback = currentExam.writingFeedback;
        currentValue = writingFeedback?.rawScore || writingFeedback?.scaledScore || writingFeedback?.overallScore || 0;
      } else {
        // Other sections are in sectionScores
        const currentSection = currentExam.sectionScores?.[section.key];
        if (currentSection) {
          currentValue = currentSection.rawScore || currentSection.score || currentSection || 0;
        }
      }

      return {
        subject: section.label,
        average: parseFloat(avgValue) || 0,
        current: parseFloat(currentValue) || 0,
        fullMark: 100
      };
    });

  // Debug
  console.log('📊 Processed radar data with writing:', radarData);

  return radarData;
}



  /**
   * Identify strengths and weaknesses
   * @param {Object} sectionMetrics - Section metrics
   * @returns {Object} Strengths and weaknesses
   */
  identifyStrengthsWeaknesses(sectionMetrics) {
    const sections = Object.entries(sectionMetrics)
      .filter(([_, data]) => data.hasData)
      .map(([key, data]) => ({
        name: this.formatSectionName(key),
        key,
        score: data.average,
        trend: data.trend
      }))
      .sort((a, b) => b.score - a.score);

    // Top 2 as strengths, bottom 2 as weaknesses
    const strengths = sections.slice(0, 2);
    const weaknesses = sections.slice(-2).reverse();

    return {
      strengths,
      weaknesses
    };
  }

  /**
   * Calculate time spent trends
   * @param {Array} examHistory - Array of exam results
   * @returns {Object} Time analysis
   */
  calculateTimeAnalysis(examHistory) {
    if (!examHistory || examHistory.length === 0) {
      return {
        averageTime: 0,
        chartData: []
      };
    }

    const times = examHistory.map(exam => exam.timeSpent || 0);
    const averageTime = times.reduce((sum, time) => sum + time, 0) / times.length;

    const chartData = examHistory.map((exam, index) => ({
      examNumber: index + 1,
      date: this.formatDate(exam.completedAt),
      timeSpent: Math.round((exam.timeSpent || 0) / 60) // Convert to minutes
    }));

    return {
      averageTime: Math.round(averageTime / 60), // Convert to minutes
      chartData
    };
  }

  /**
   * Get comparison with previous exam
   * @param {Object} currentExam - Current exam results
   * @param {Array} examHistory - Previous exam results
   * @returns {Object} Comparison data
   */
  compareWithPrevious(currentExam, examHistory) {
    if (!currentExam || !examHistory || examHistory.length === 0) {
      return {
        hasComparison: false,
        scoreDifference: 0,
        timeDifference: 0,
        sectionComparisons: {}
      };
    }

    const previousExam = examHistory[examHistory.length - 1];
    const scoreDifference = (currentExam.overallScore || 0) - (previousExam.overallScore || 0);
    const timeDifference = (currentExam.timeSpent || 0) - (previousExam.timeSpent || 0);

    const sectionComparisons = {};
    ['reading', 'listening', 'writing', 'speaking', 'grammar'].forEach(section => {
      const currentScore = currentExam.sectionScores?.[section] || 0;
      const previousScore = previousExam.sectionScores?.[section] || 0;

      if (currentScore > 0 && previousScore > 0) {
        sectionComparisons[section] = currentScore - previousScore;
      }
    });

    return {
      hasComparison: true,
      scoreDifference: Math.round(scoreDifference * 10) / 10,
      timeDifference: Math.round(timeDifference / 60), // Convert to minutes
      sectionComparisons
    };
  }

  /**
   * Format timestamp to readable date
   * @param {Object} timestamp - Firestore timestamp
   * @returns {string} Formatted date
   */
  formatDate(timestamp) {
    if (!timestamp) return 'N/A';

    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'N/A';
    }
  }

  /**
   * Format section name for display
   * @param {string} sectionKey - Section key
   * @returns {string} Formatted name
   */
  formatSectionName(sectionKey) {
    const names = {
      reading: 'Reading',
      listening: 'Listening',
      writing: 'Writing',
      speaking: 'Speaking',
      grammar: 'Grammar'
    };
    return names[sectionKey] || sectionKey;
  }

  /**
   * Get empty section data structure
   * @returns {Object} Empty section data
   */
  getEmptySectionData() {
    return {
      average: 0,
      lastScore: 0,
      trend: 'stable',
      chartData: [],
      hasData: false
    };
  }
}

// Export singleton instance
const examAnalyticsService = new ExamAnalyticsService();
export default examAnalyticsService;